import {power, isDcPower} from './power.mjs';
import {POINT_SHORT_KEYS} from './i18n-budget.mjs';
import {animateDisclosure} from './disclosure.mjs';
import {t, day} from './i18n.mjs';
import {S} from './strings.mjs';
import {plugIcon} from './plug-icons.mjs';
import {icon} from './icons.mjs';
import {bindPointId, sharedPrefix, fitPointIds, closePointId} from './point-ids.mjs';
export * from './details-model.mjs';
export const TITLE_MIN_FONT_PX = 15;

const el = (tag, className, text) => { const e = document.createElement(tag); if (className) e.className = className; if (text !== undefined && text !== null) e.textContent = text; return e; };
let groupSeq = 0;
const groupKey = g => `${[...g.plugs].sort().join('/')}|${g.kw}|${g.current}|${g.priceKey ?? ''}`;
// One fee row: plain name | amount, with its self-contained conditions below.
function priceRow(r) {
  const row = el('div', 'price-row is-' + r.kind + (r.sub ? ' is-sub' : ''));
  const name = el('span', 'price-name');
  name.append(el('span', 'price-label', r.label));
  row.append(name);
  if (r.amount) {
    const amount = el('span', 'price-amount');
    // Explicit range boundary: each amount/unit stays whole, independently of OS hyphenation.
    const parts = r.amount.split('–');
    parts.forEach((part, i) => {
      amount.append(el('span', 'price-value', part + (i < parts.length - 1 ? '–' : '')));
      if (i < parts.length - 1) amount.append(el('wbr'));
    });
    row.append(amount);
  }
  if (r.note) row.append(el('span', 'price-cond', r.note));
  return row;
}
// Power-chip head: neutral connector tile, name, power and short availability.
// The whole head is the disclosure button (>= 44 px), drawn as an overlay so the chevron can stay small.
function plugCard(g, perPoint, expanded = false, suppressStatus = false) {
  const card = el('li', 'plug-card tone-' + g.tone);
  const head = el('div', 'plug-head');
  const glyph = el('div', 'plug-glyph' + (g.plugs.length > 1 ? ' is-multi' : ''));
  const icons = g.plugs.map(plugIcon).filter(Boolean);
  glyph.innerHTML = icons.length ? icons.slice(0, 2).join('') : '<span class="plug-badge" aria-hidden="true">?</span>';
  const main = el('div', 'plug-main');
  const top = el('div', 'plug-top');
  const title = el('p', 'plug-title');
  title.append(glyph, el('span', 'plug-name', g.name));
  if (g.kw) title.append(el('span', 'plug-power' + (isDcPower(g.current, g.kw) ? ' is-dc' : ''), S.kw(power(g.kw))));
  const status = el('span', 'plug-status dist dist-' + g.statusTone, suppressStatus ? '' : g.statusShort ?? g.status);
  const groupAge = g.fresh.age ? t('siteUpdateInfo', {age: g.fresh.age}) : S.noUpdate;
  // Retain both forms for accessible descriptions; display the compact count in the head.
  if (status.dataset) { status.dataset.full = suppressStatus ? '' : g.status; status.dataset.short = suppressStatus ? '' : g.statusShort ?? g.status; }
  top.append(title, status);
  main.append(top);
  if (perPoint) {
    const price = el('div', 'plug-price' + (g.priceRows ? '' : ' is-missing'));
    if (g.priceRows) {
      const list = el('div', 'price-rows');
      // Several rows of one fee (e.g. energy by time of day): the name once, then one indented row per condition.
      g.priceRows.forEach((r, i, rows) => {
        const many = rows.filter(x => x.kind === r.kind).length > 1, first = rows.findIndex(x => x.kind === r.kind) === i;
        if (many && first) list.append(priceRow({...r, amount: '', note: ''}));
        list.append(many ? priceRow({...r, label: r.note || t('otherTimes'), note: '', sub: true}) : priceRow(r));
      });
      price.append(list);
    } else price.textContent = S.priceNotReported;
    main.append(price);
  }
  const chevron = el('span', 'plug-chevron'); chevron.innerHTML = icon('chevron');
  if (chevron.setAttribute) chevron.setAttribute('aria-hidden', 'true');
  // Disclosure: one row per charge point with its id, occupancy and observation duration.
  const listId = 'plug-points-' + (++groupSeq);
  const toggle = el('button', 'plug-toggle');
  toggle.type = 'button';
  toggle.setAttribute('aria-expanded', String(expanded)); toggle.setAttribute('aria-controls', listId);
  toggle.setAttribute('aria-label', S.pointCount(g.total) + ' · ' + groupAge + (suppressStatus ? ' · ' + g.status : ''));
  toggle.dataset.group = groupKey(g);
  const rows = el('ul', 'plug-points');
  rows.id = listId; rows.hidden = !expanded; rows.setAttribute('aria-label', S.pointsList);
  const ids = g.points.map(p => p.id), prefix = sharedPrefix(ids);
  for (const p of g.points) {
    // Three columns (id | occupancy | duration), shared by all rows so they line up.
    const row = el('li', 'plug-point');
    const id = el('span', 'plug-point-id' + (p.id ? ' u-text' : ' is-generic'), p.label);
    id.setAttribute('aria-label', p.label);
    if (p.id) {
      id.dataset.id = p.id; id.dataset.ids = JSON.stringify(ids); id.dataset.prefix = prefix; bindPointId(id, p.id);
    }
    row.append(id);
    const state = el('span', 'dist dist-' + p.state);
    state.title = p.text; state.setAttribute('aria-label', p.text);
    state.dataset.full = p.text; state.dataset.short = t(POINT_SHORT_KEYS[p.state]);
    state.append(el('i', null), p.text);
    const since = el('span', 'plug-point-since', p.since ?? '');
    row.append(state, since);
    rows.append(row);
  }
  toggle.onclick = () => { const open = toggle.getAttribute('aria-expanded') !== 'true'; toggle.setAttribute('aria-expanded', String(open)); animateDisclosure(rows, open); card.classList?.toggle('is-open', open); if (open && globalThis.requestAnimationFrame) requestAnimationFrame(() => fitCards(card)); };
  if (expanded) card.classList?.add('is-open');
  head.append(main, chevron, toggle);
  card.append(head, rows);
  return card;
}
// Measure IDs in the current font and compensate the symmetric native scrollbar gutter.
export function fitCards(box) {
  const body = box.closest?.('.sheet-body');
  if (body) body.style.setProperty('--sheet-gutter', ((body.offsetWidth - body.clientWidth) / 2) + 'px');
  for (const state of box.querySelectorAll?.('.plug-point > .dist') ?? []) {
    const dot=state.querySelector('i');
    state.replaceChildren(dot, state.dataset.full);
    if (state.scrollWidth > state.clientWidth + 1) state.replaceChildren(dot, state.dataset.short);
  }
  for (const card of box.querySelectorAll?.('.plug-card') ?? []) {
    const status = card.querySelector('.plug-status'), title = card.querySelector('.plug-title');
    if (!status || !title) continue;
    status.textContent = status.dataset.full;
    if (title.scrollWidth + status.scrollWidth + 8 > status.parentElement.clientWidth) status.textContent = status.dataset.short;
  }
  fitPointIds(box);
}
// Keep action words whole. Share alone may fall back to its accessible icon.
export function fitActions(button) {
  if (!button || button.hidden) return;
  const label = button.querySelector('span');
  if (!label) return;
  label.hidden = false;
  const text = label.textContent;
  button.setAttribute('aria-label', text);
  button.title = text;
  label.hidden = label.scrollWidth > label.clientWidth;
}
function chip(className, iconName, text, title) {
  const c = el('span', 'site-chip ' + className);
  if (iconName) { const i = el('span', 'site-chip-icon'); i.innerHTML = icon(iconName); c.append(i); } else c.append(el('i', 'fresh-dot'));
  c.append(el('span', null, text));
  if (title) c.title = title;
  return c;
}
// Clear site-specific fitting and tooltip state before showing a generic or new title.
export function resetTitle(title, text) {
  if (!title) return;
  title.textContent = text;
  title.removeAttribute('title');
  title.style.fontSize = '';
  title.classList.remove('is-wrapped');
}
// Run synchronously before paint; reset on every fit so wider viewports restore the base size.
export function fitTitle(title, m) {
  if (!title) return;
  resetTitle(title, m.title);
  title.title = m.title;
  const base = parseFloat(getComputedStyle(title).fontSize);
  let size = base;
  while (title.scrollWidth > title.clientWidth && size > TITLE_MIN_FONT_PX) {
    size = Math.max(TITLE_MIN_FONT_PX, size - 0.5);
    title.style.fontSize = size + 'px';
  }
  if (title.scrollWidth > title.clientWidth) title.classList.add('is-wrapped');
}
export function renderDetails(m, box) {
  closePointId();
  // Groups start collapsed, even a single one (Max, 01.11.: one group can hold 40 points); same-site refresh keeps the state.
  const same = box.dataset?.site === String(m.id);
  const opened = same ? new Set([...(box.querySelectorAll?.('.plug-toggle[aria-expanded="true"]') ?? [])].map(b => b.dataset.group)) : new Set();
  if (box.dataset) box.dataset.site = String(m.id);
  const head = el('div', 'site-head');
  const labels = el('dl', 'site-labels');
  for (const [label, value] of [[S.placeLabel, m.place], [S.operatorLabel, m.operator]]) {
    if (value) {
      const line = el('div', 'site-label-line');
      line.append(el('dt', 'site-label', label + ':'), ' ', el('dd', 'u-text', value));
      labels.append(line);
    }
  }
  if (labels.children.length) head.append(labels);
  const chips = el('div', 'site-chips');
  chips.append(chip('site-fresh is-' + m.fresh.kind, null, m.fresh.text, m.fresh.age ? t('siteUpdateInfo', {age: m.fresh.age}) : m.registry ? t('registryChipInfo') : S.noUpdate));
  if (m.allOut) chips.append(chip('is-out', null, t('out')));
  if (m.distanceAway) chips.append(chip('is-distance', 'navigation', m.distanceAway));
  if (m.openingWarning) chips.append(chip('is-warn', 'clock', m.openingWarning));
  head.append(chips);

  const plugs = el('section', 'site-section');
  plugs.setAttribute('aria-label', S.plugs);
  if (m.groups.length) {
    const list = el('ul', 'plug-list');
    for (const g of m.groups) list.append(plugCard(g, m.perPointPrices, same ? opened.has(groupKey(g)) : false, m.allOut));
    plugs.append(list);
  } else plugs.append(el('p', 'site-notice', S.noPoints));

  // Details: always open; place and operator stay in the head.
  const more = el('section', 'site-more');
  more.setAttribute('aria-label', S.moreInfo);
  more.append(el('h3', 'site-more-title', S.moreInfo));
  const facts = el('dl', 'site-facts');
  const fact = (key, full, value) => {
    if (!value) return;
    const label = el('dt', null, t(key)); label.title = full; label.setAttribute('aria-label', full);
    facts.append(label, el('dd', 'u-text', value));
  };
  fact('detailOpening', S.opening, m.opening === '24/7' ? S.open247 : m.opening ?? S.noInfo);
  if (m.sitePrice) fact('detailPrice', S.price, [m.sitePrice, m.sitePriceExtra].filter(Boolean).join('\u00a0· '));
  fact('detailSource', S.dataSource, m.source ?? m.operator);
  if (m.registry?.access) fact('detailAccess', t('detailAccess'), m.registry.access);
  fact('detailUpdated', S.dataTime, m.registry ? [t('registryAsOf', {date: day(m.registry.asOf)}), m.registry.stale && t('registryStale')].filter(Boolean).join('\u00a0· ') : m.reported ?? S.noUpdate);
  fact('detailSiteId', S.siteId, m.id);
  more.append(facts);
  box.replaceChildren(head, plugs, more);
}
